import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Clock,
  DollarSign,
  Edit,
  ExternalLink,
  RefreshCw,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import {
  checkInAttendee,
  deleteEvent,
  getEventAttendees,
  getEventDetails,
  getEventWaitlist,
  updateEventStatus,
} from '../api/services';
import {
  formatCurrency,
  formatDate,
  formatTime,
  getApiErrorMessage,
  getDisplayName,
  getInitials,
  slugToLabel,
} from '../utils/admin';

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'registration_open', label: 'Registration Open' },
  { value: 'registration_closed', label: 'Registration Closed' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [attendeePagination, setAttendeePagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });

  const fetchEventDetails = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getEventDetails(eventId);
      setEvent(response.data || null);
      setWaitlist(response.data?.waitlist || []);
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load event details'));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchAttendees = useCallback(
    async (page = attendeePagination.page) => {
      try {
        const response = await getEventAttendees(eventId, {
          page,
          limit: attendeePagination.limit,
        });
        setAttendees(response.data || []);
        setAttendeePagination({
          page: response.pagination?.page || page,
          limit: response.pagination?.limit || attendeePagination.limit,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 1,
        });
      } catch (error) {
        console.error('Failed to fetch attendees:', error);
        toast.error(getApiErrorMessage(error, 'Failed to load attendees'));
      }
    },
    [attendeePagination.limit, attendeePagination.page, eventId]
  );

  const fetchWaitlist = useCallback(async () => {
    try {
      const response = await getEventWaitlist(eventId);
      setWaitlist(response.data || []);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load waitlist'));
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  useEffect(() => {
    if (activeTab === 'attendees') {
      fetchAttendees();
    }

    if (activeTab === 'waitlist') {
      fetchWaitlist();
    }
  }, [activeTab, fetchAttendees, fetchWaitlist]);

  const handleStatusChange = async () => {
    try {
      const response = await updateEventStatus(eventId, newStatus);
      toast.success(response.message || 'Event status updated successfully');
      setStatusModalOpen(false);
      fetchEventDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update event status'));
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteEvent(eventId, deleteReason.trim());
      toast.success(response.message || 'Event removed successfully');
      navigate('/events');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete event'));
    }
  };

  const handleCheckIn = async (userId) => {
    try {
      const response = await checkInAttendee(eventId, userId);
      toast.success(response.message || 'Attendee checked in successfully');
      fetchAttendees(attendeePagination.page);
      fetchEventDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check in attendee'));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-blue-100 text-blue-800',
      registration_open: 'bg-green-100 text-green-800',
      registration_closed: 'bg-yellow-100 text-yellow-800',
      ongoing: 'bg-purple-100 text-purple-800',
      completed: 'bg-teal-100 text-teal-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const renderListSection = (title, items, icon = null) => {
    if (!items?.length) {
      return null;
    }

    return (
      <div>
        <h3 className="mb-3 text-lg font-semibold text-gray-900">{title}</h3>
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
              {icon}
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getAttendeeStatus = (attendee) => {
    if (attendee.checkedIn || attendee.attended || attendee.checkedInAt) {
      return 'checked_in';
    }

    return attendee.status || 'registered';
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="rounded-xl bg-white p-8 text-center shadow-sm">
        <p className="text-gray-500">Event not found.</p>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const owner = event.eventType === 'user_meetup' ? event.hostDetails : event.createdByDetails;
  const currentPrice = event.currentPrice ?? event.price ?? 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          onClick={() => navigate('/events')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-1" /> Back to Events
        </button>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/events/${eventId}/edit`)}
            className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Edit size={16} className="mr-2" /> Edit
          </button>
          <button
            onClick={() => setDeleteModalOpen(true)}
            className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <Trash2 size={16} className="mr-2" /> Delete / Cancel
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        {event.coverImage && (
          <div className="h-56 bg-gray-100">
            <img src={event.coverImage} alt={event.title} className="h-full w-full object-cover" />
          </div>
        )}

        <div className="space-y-6 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(event.status)}`}>
                  {slugToLabel(event.status)}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {slugToLabel(event.eventType)}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {slugToLabel(event.category)}
                </span>
              </div>
              <h1 className="mt-3 text-3xl font-bold text-gray-900">{event.title}</h1>
              <p className="mt-2 max-w-3xl whitespace-pre-wrap text-sm text-gray-600">
                {event.description}
              </p>
            </div>

            <button
              onClick={() => {
                setNewStatus(event.status);
                setStatusModalOpen(true);
              }}
              className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw size={16} className="mr-2" /> Change Status
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center text-sm text-gray-500">
                <Calendar size={16} className="mr-2" /> Date
              </div>
              <p className="mt-2 font-semibold text-gray-900">{formatDate(event.date)}</p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center text-sm text-gray-500">
                <Clock size={16} className="mr-2" /> Time
              </div>
              <p className="mt-2 font-semibold text-gray-900">
                {formatTime(event.startTime)} - {formatTime(event.endTime)}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center text-sm text-gray-500">
                <Users size={16} className="mr-2" /> Capacity
              </div>
              <p className="mt-2 font-semibold text-gray-900">
                {event.currentAttendees || 0} / {event.maxAttendees || 0}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Spots remaining: {event.spotsRemaining ?? 'N/A'}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center text-sm text-gray-500">
                <DollarSign size={16} className="mr-2" /> Current Price
              </div>
              <p className="mt-2 font-semibold text-gray-900">
                {currentPrice > 0 ? formatCurrency(currentPrice, event.currency) : 'Free'}
              </p>
              {event.earlyBirdPrice ? (
                <p className="mt-1 text-xs text-gray-500">
                  Early bird until {formatDate(event.earlyBirdDeadline)}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100">
          <nav className="flex flex-wrap">
            {['details', 'attendees', 'waitlist'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 text-sm font-medium capitalize ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
                {tab === 'attendees' && ` (${event.currentAttendees || 0})`}
                {tab === 'waitlist' && ` (${event.waitlistCount || waitlist.length || 0})`}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
              <div className="space-y-6">
                <section>
                  <h2 className="mb-3 text-lg font-semibold text-gray-900">Venue</h2>
                  <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                    <p className="font-medium text-gray-900">{event.venue?.name || 'Venue not set'}</p>
                    <p className="mt-1">{event.venue?.city || 'City not set'}</p>
                    {event.venue?.googleMapsLink && (
                      <a
                        href={event.venue.googleMapsLink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink size={16} className="mr-1" /> Open in Google Maps
                      </a>
                    )}
                    {event.venue?.coordinates && (
                      <p className="mt-3 text-xs text-gray-500">
                        Coordinates: {event.venue.coordinates.latitude}, {event.venue.coordinates.longitude}
                      </p>
                    )}
                  </div>
                </section>

                {renderListSection(
                  'What to Expect',
                  event.whatToExpect,
                  <AlertCircle size={16} className="mt-0.5 text-blue-500" />
                )}
                {renderListSection(
                  'Highlights',
                  event.highlights,
                  <AlertCircle size={16} className="mt-0.5 text-green-500" />
                )}
                {renderListSection(
                  'Requirements',
                  event.requirements,
                  <AlertCircle size={16} className="mt-0.5 text-orange-500" />
                )}

                {event.galleryImages?.length ? (
                  <section>
                    <h2 className="mb-3 text-lg font-semibold text-gray-900">Gallery</h2>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {event.galleryImages.map((image) => (
                        <img
                          key={image}
                          src={image}
                          alt={event.title}
                          className="h-32 w-full rounded-lg object-cover"
                        />
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>

              <div className="space-y-6">
                <section className="rounded-xl bg-gray-50 p-5">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {event.eventType === 'user_meetup' ? 'Host Details' : 'Created By'}
                  </h2>
                  {owner ? (
                    <div className="mt-4 flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                        {getInitials(owner)}
                      </div>
                      <div className="text-sm text-gray-600">
                        <p className="font-medium text-gray-900">{getDisplayName(owner)}</p>
                        <p>{owner.phoneNumber || 'No phone available'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500">Owner information is unavailable.</p>
                  )}
                </section>

                <section className="rounded-xl border border-gray-100 p-5">
                  <h2 className="text-lg font-semibold text-gray-900">Registration Settings</h2>
                  <div className="mt-4 space-y-3 text-sm text-gray-600">
                    <p>Registration deadline: {formatDate(event.registrationDeadline)}</p>
                    <p>Waitlist enabled: {event.allowWaitlist ? 'Yes' : 'No'}</p>
                    <p>Auto-promote waitlist: {event.autoPromoteFromWaitlist ? 'Yes' : 'No'}</p>
                    <p>Published at: {formatDate(event.publishedAt)}</p>
                  </div>
                </section>

                {event.customTags?.length ? (
                  <section className="rounded-xl border border-gray-100 p-5">
                    <h2 className="text-lg font-semibold text-gray-900">Custom Tags</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {event.customTags.map((tag) => (
                        <span key={tag} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </section>
                ) : null}

                {event.eventType === 'user_meetup' && (
                  <section className="rounded-xl border border-gray-100 p-5">
                    <h2 className="text-lg font-semibold text-gray-900">Meetup Preferences</h2>
                    <div className="mt-4 space-y-3 text-sm text-gray-600">
                      <p>Looking for: {event.lookingFor || 'N/A'}</p>
                      <p>Skill level: {slugToLabel(event.skillLevel)}</p>
                      <p>
                        Preferred age range:{' '}
                        {event.preferredAgeRange
                          ? `${event.preferredAgeRange.min} - ${event.preferredAgeRange.max}`
                          : 'N/A'}
                      </p>
                      <p>Gender preference: {slugToLabel(event.genderPreference)}</p>
                      <p>Visibility: {slugToLabel(event.visibility)}</p>
                      {event.visibility === 'private' && (
                        <>
                          <p>Invite code: {event.inviteCode || 'N/A'}</p>
                          <p>
                            Invite uses: {event.inviteCodeUses || 0} / {event.inviteCodeLimit || 0}
                          </p>
                        </>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </div>
          )}

          {activeTab === 'attendees' && (
            <div className="space-y-4">
              {attendees.length === 0 ? (
                <p className="py-8 text-center text-gray-500">No attendees yet.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Attendee</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Phone</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Registered</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {attendees.map((attendee) => {
                          const attendeeStatus = getAttendeeStatus(attendee);
                          const attendeeName = getDisplayName(attendee.userDetails, 'Unknown attendee');
                          const canCheckIn =
                            attendee.status === 'registered' && attendeeStatus !== 'checked_in';

                          return (
                            <tr key={attendee._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  {attendee.userDetails?.profilePhotoUrl ? (
                                    <img
                                      src={attendee.userDetails.profilePhotoUrl}
                                      alt={attendeeName}
                                      className="h-10 w-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                                      {getInitials(attendee.userDetails)}
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-gray-900">{attendeeName}</p>
                                    <p className="text-xs text-gray-500">
                                      {attendee.userDetails?.profession || 'Profession unavailable'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {attendee.userDetails?.phoneNumber || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {attendee.userDetails?.city || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatDate(attendee.registeredAt)}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                    attendeeStatus === 'checked_in'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}
                                >
                                  {slugToLabel(attendeeStatus)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {canCheckIn ? (
                                  <button
                                    onClick={() => handleCheckIn(attendee.userDetails?._id)}
                                    className="inline-flex items-center text-sm text-green-600 hover:text-green-800"
                                  >
                                    <UserCheck size={16} className="mr-1" /> Check In
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">No actions</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {attendeePagination.pages > 1 && (
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => fetchAttendees(attendeePagination.page - 1)}
                        disabled={attendeePagination.page === 1}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-500">
                        Page {attendeePagination.page} of {attendeePagination.pages}
                      </span>
                      <button
                        onClick={() => fetchAttendees(attendeePagination.page + 1)}
                        disabled={attendeePagination.page === attendeePagination.pages}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'waitlist' && (
            <div className="space-y-3">
              {waitlist.length === 0 ? (
                <p className="py-8 text-center text-gray-500">No waitlist entries.</p>
              ) : (
                waitlist.map((entry, index) => {
                  const userDetails = entry.userDetails || {};
                  const entryName = getDisplayName(userDetails, 'Unknown user');

                  return (
                    <div
                      key={entry._id}
                      className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-700">
                          #{entry.queuePosition || index + 1}
                        </span>
                        {userDetails.profilePhotoUrl ? (
                          <img
                            src={userDetails.profilePhotoUrl}
                            alt={entryName}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-sm font-semibold text-gray-600">
                            {getInitials(userDetails)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{entryName}</p>
                          <p className="text-sm text-gray-500">
                            {userDetails.profession || 'Profession unavailable'} in{' '}
                            {userDetails.city || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div className="text-sm text-gray-500">
                        <p>{userDetails.phoneNumber || 'No phone number'}</p>
                        <p>Added {formatDate(entry.addedAt)}</p>
                      </div>

                      <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                        {slugToLabel(entry.status)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Change Event Status"
      >
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={newStatus}
              onChange={(event) => setNewStatus(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setStatusModalOpen(false)}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleStatusChange}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Update Status
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete or Cancel Event"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {event.currentAttendees > 0
              ? 'This event has attendees. The backend will cancel it and notify attendees instead of permanently deleting it.'
              : 'This event has no attendees. The backend will permanently delete it.'}
          </p>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Reason (optional)
            </label>
            <textarea
              value={deleteReason}
              onChange={(event) => setDeleteReason(event.target.value)}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add a cancellation reason"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              {event.currentAttendees > 0 ? 'Cancel Event' : 'Delete Event'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetails;
